import { useContext, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import * as R from 'ramda';

import { GET_USERS_WITH_TIME_SHEET_ENTRIES } from '@atom/graph/user';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import {
  TimeEntriesConnectionInput,
  TimeEntriesTotalDurationInput,
} from '@atom/types/timeEntry';
import { UsersConnection, UsersConnectionInput } from '@atom/types/user';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import TeamContext from '../TeamContext';
import ListBulkControls from '../timeHeaders/ListBulkControls';
import PayPeriodControls from '../timeHeaders/PayPeriodControls';
import TitleHeader from '../timeHeaders/TitleHeader';

import TimeSheetListTable from './TimeSheetListTable';

const TimeSheetList = () => {
  const { activeTimeSheetGroup, selectedPayPeriod } = useContext(TeamContext);
  const [{ page, limit }] = useNavigation({
    page: SearchParamType.NUMBER,
    limit: SearchParamType.NUMBER,
  });

  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    new Set(),
  );

  const [
    getUsersWithTimeSheets,
    { loading, data, refetch: refetchUsersWithTimeSheetEntries },
  ] = useLazyQuery<
    { users: UsersConnection },
    {
      input: UsersConnectionInput;
      timeSheetEntriesInput: TimeEntriesConnectionInput;
      timeEntriesTotalDurationInput: TimeEntriesTotalDurationInput;
    }
  >(GET_USERS_WITH_TIME_SHEET_ENTRIES, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: () => setSelectedUserIds(new Set()),
  });

  useEffect(() => {
    if (!R.isEmpty(activeTimeSheetGroup) && !R.isEmpty(selectedPayPeriod)) {
      getUsersWithTimeSheets({
        variables: {
          input: {
            page,
            limit,
            userGroupId: activeTimeSheetGroup?.id,
            showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
            userGroupStartDate: selectedPayPeriod?.startDate,
            userGroupEndDate: selectedPayPeriod?.endDate,
          },
          timeSheetEntriesInput: {
            userGroupId: activeTimeSheetGroup?.id,
            dateStart: selectedPayPeriod?.startDate,
            dateEnd: selectedPayPeriod?.endDate,
          },
          timeEntriesTotalDurationInput: {
            dateStart: selectedPayPeriod?.startDate,
            dateEnd: selectedPayPeriod?.endDate,
          },
        },
      });
    }
  }, [page, limit, activeTimeSheetGroup, selectedPayPeriod]);

  useEffect(() => {
    if (selectedPayPeriod) {
      setSelectedUserIds(new Set());
    }
  }, [selectedPayPeriod]);

  const showBulkControls = selectedUserIds.size > 0;

  return (
    <>
      <TitleHeader title={activeTimeSheetGroup?.groupPath?.join(' / ')} />
      {showBulkControls ? (
        <ListBulkControls
          users={data?.users?.users}
          loading={loading}
          selectedUserIds={selectedUserIds}
          setSelectedUserIds={setSelectedUserIds}
          refetchUsersWithTimeSheetEntries={refetchUsersWithTimeSheetEntries}
        />
      ) : (
        <PayPeriodControls loading={loading} />
      )}
      <TimeSheetListTable
        users={data?.users?.users}
        totalCount={data?.users?.totalCount}
        setSelectedUserIds={setSelectedUserIds}
        selectedUserIds={selectedUserIds}
        loading={loading}
      />
    </>
  );
};

export default TimeSheetList;
